import { Component, OnInit, ElementRef } from '@angular/core';
import {
	AbstractModal,
	BroadcastService,
	ModalButtonModel,
	ModalButtonType,
	ObjectUtils
} from '@cv-invision/core';
import { CIJobTypeModel, ADD_EDIT_CI_JOB_TYPE } from '../model/ci-job-types.model';
import { GlobalConfigurationState } from '../../state/global-configuration.state';
import { Store } from '@ngxs/store';

@Component({
	selector: 'cv-invision-create-update-ci-job-type',
	templateUrl: './create-update-ci-job-type.component.html',
	styleUrls: ['./create-update-ci-job-type.component.scss']
})
export class CreateUpdateCIJobTypeComponent extends AbstractModal implements OnInit {
	ciJobTypesConfiguration: Array<CIJobTypeModel>;
	originalCIJobTypeData: CIJobTypeModel;
	ciJobTypeData: CIJobTypeModel;
	saving: boolean;
	originsList: Array<any>;
	originSelected: any;
	appScopeProductList: Array<any>;
	acuityProductList: Array<any>;
	cmpAcuityProductList: Array<any>;
	productListOptions: Array<any>;
	productSelected: Array<any>;
	formTypesList: Array<any>;
	formTypeSelected: any;

	constructor(
		private store: Store,
		private broadcastService: BroadcastService,
		private elementRef: ElementRef
	) {
		super();
		this.ciJobTypeData = new CIJobTypeModel();
		this.originalCIJobTypeData = new CIJobTypeModel();
		this.saving = false;
		this.productSelected = [];
		this.productListOptions = [];
	}

	ngOnInit(): void {
		this.originsList = this.store.selectSnapshot(
			GlobalConfigurationState.getOrigins
		);

		this.appScopeProductList = this.store.selectSnapshot(
			GlobalConfigurationState.getAppScopeProducts
		).map((product) => {
			return {
				id: product.id,
				name: `(${product.id}) ${product.title}`
			};
		});

		this.acuityProductList = this.store.selectSnapshot(
			GlobalConfigurationState.getAcuityProducts
		).map((product) => {
			return {
				id: product.id,
				name: `(${product.id}) ${product.title}`
			};
		});

		this.cmpAcuityProductList = this.store.selectSnapshot(
			GlobalConfigurationState.getCmpAcuityProducts
		).map((product) => {
			return {
				id: product.id,
				name: `(${product.id}) ${product.title}`
			};
		});

		this.formTypesList = this.store.selectSnapshot(
			GlobalConfigurationState.getFormTypes
		);

		if (this.data.isEditing) {
			this.ciJobTypeData = ObjectUtils.recursiveDeepCopy(this.data.ciJobTypeData);
			this.originalCIJobTypeData = ObjectUtils.recursiveDeepCopy(this.ciJobTypeData);
			this.originSelected = {
				id: this.ciJobTypeData.origin,
				name: this.ciJobTypeData.origin
			};

			this.setProductsByOrigin(this.ciJobTypeData.origin);

			this.productSelected = [{
				id: this.ciJobTypeData.productId,
				name: this.ciJobTypeData.productName
			}];

			this.formTypeSelected = {
				id: this.ciJobTypeData.formId,
				name: this.ciJobTypeData.formName
			};
		}

		const cancelButton: ModalButtonModel = {
			name: 'close',
			text: 'Close',
			class: 'btn-sm',
			type: ModalButtonType.ACTION,
			action: this.onCancel.bind(this)
		};
		const confirmButton: ModalButtonModel = {
			name: 'save',
			text: 'Save',
			class: 'btn-sm cv-btn-blue ',
			type: ModalButtonType.ACTION,
			disabled: (): boolean => !this.setIsFormValid() || this.saving,
			action: () => this.onSave(),
			loading: (): boolean => this.saving,
			loadingText: 'Saving'
		};

		this.buttons.push(cancelButton);
		this.buttons.push(confirmButton);
	}

	/**
	 * On Save button.
	 */
	onSave(): void {
		this.saving = true;
		let data = null;
		let index = null;
		if (this.data.isEditing) {
			const editCIJobTypeData = Object.assign({}, this.ciJobTypeData);
			delete editCIJobTypeData.id;

			data = editCIJobTypeData;
			index = this.ciJobTypeData.id;
		} else {
			data = this.ciJobTypeData;
		}

		this.broadcastService.broadcast({
			name: ADD_EDIT_CI_JOB_TYPE,
			event: {
				isEditing: this.data.isEditing,
				index: index,
				data: data
			}
		});

		super.onAcceptSuccess({
			success: true
		});
		this.saving = false;
	}

	/**
	 * Select assign to
	 * @param $event
	 */
	onOriginSelected($event: any): void {
		this.originSelected = $event ? $event : null;
		this.ciJobTypeData.origin = $event ? $event.name : null;

		this.productSelected = [];
		this.ciJobTypeData.productId = null;
		this.ciJobTypeData.productName = null;

		this.setProductsByOrigin($event.id);

		const clearButton = this.elementRef.nativeElement.querySelector('.product-dropdown .__clear-button');

		if (clearButton) {
			clearButton.click();
		}
	}

	/**
	 * Select assign to
	 * @param $event
	 */
	onProductSelected($event: any): void {
		this.ciJobTypeData.productId = $event ? $event.id : null;
		this.ciJobTypeData.productName = $event ? $event.name : null;
	}

	/**
	 * Select assign to
	 * @param $event
	 */
	onFormTypeSelected($event: any): void {
		this.ciJobTypeData.formId = $event ? $event.id : null;
		this.ciJobTypeData.formName = $event ? $event.name : null;
	}

	/**
	 * set value for hybrid Product
	 * @param isHybridProduct
	 */
	setHybridProduct(isHybridProduct: boolean): void {
		this.ciJobTypeData.isHybridProduct = isHybridProduct;
	}

	/**
	 * Is form valid?
	 */
	setIsFormValid(): boolean {
		return this.ciJobTypeData.origin &&
			this.ciJobTypeData.productId &&
			this.ciJobTypeData.formId &&
			this.isDirty();
	}

	/**
	 * Is ci job type data dirty?
	 */
	isDirty(): boolean {
		return JSON.stringify(this.originalCIJobTypeData) !== JSON.stringify(this.ciJobTypeData);
	}

	onDismiss(): void {
		super.onCancel();
	}

	setProductsByOrigin(origin: string): void {
		switch (origin.toUpperCase()) {
			case 'APPSCOPE':
				this.productListOptions = this.appScopeProductList;
				break;
			case 'ACUITY':
				this.productListOptions = this.acuityProductList;
				break;
			case 'CMPACUITY':
				this.productListOptions = this.cmpAcuityProductList;
				break;
		}
	}
}
